import { sendToApi } from 'api/client'
import { LayerSummary, PaginationParams, UserCard } from 'types'
import { Timeline } from 'screens/Activity/Types'
import { ChannelType } from 'components/CampaignCreationModal/Types'

export enum InformalFeedbackRequestState {
  Open = 'open',
  Closed = 'closed',
}

export enum InformalFeedbackRequestType {
  Request = 'request',
  ViewRequest = 'view_request',
  Approve = 'approve',
  Reject = 'reject',
  Feedback = 'feedback',
  Close = 'close',
  Dismiss = 'dismiss',
}

export enum InformalFeedbackReviewState {
  Pending = 'pending',
  Submitted = 'submitted',
  Dismissed = 'dismissed',
}

export enum InformalFeedbackViewerState {
  Pending = 'pending',
  Completed = 'completed',
}

export interface InformalFeedbackRequestUserCard extends UserCard {
  id: number
}

export interface InformalFeedbackViewerUserCard extends UserCard {
  id: number
}

export interface BaseInformalFeedbackRequestViewer {
  id: number
  state: InformalFeedbackViewerState
  completed_at: string | null
  viewer: InformalFeedbackViewerUserCard
}

export interface CompletedViewer extends BaseInformalFeedbackRequestViewer {
  state: InformalFeedbackViewerState.Completed
  completed_at: string
}

export interface PendingViewer extends BaseInformalFeedbackRequestViewer {
  state: InformalFeedbackViewerState.Pending
  completed_at: null
}

export type InformalFeedbackRequestViewer = CompletedViewer | PendingViewer

export interface InformalFeedbackRequestArticle {
  is_branch: boolean
  key: string
  url: string
  title: string
  channel_emoji?: ChannelType | null
  slug: string
  header_emoji: string | null
  external_file_icon: string | null
}

export interface InformalFeedbackRequestDesign {
  key: string
  url: string
  title: string
  channel?: string
}

export interface InformalFeedbackRequestAlmanacFile {
  id: string
}

export interface InformalFeedbackReview {
  id: number
  informal_feedback_request_id: number
  state: InformalFeedbackReviewState
  informal_feedback_request_message: string | null
  message?: string | null
  acknowledged_last_transition?: boolean
  reviewer: InformalFeedbackRequestUserCard
  seeded: boolean
  comments?: { id: number }[]
  todos?: { id: number }[]
  score: InformalFeedbackReviewScore | null
  last_viewed_at?: string | null
  updated_since_last_viewed?: boolean
  submitted_at: string | null
  required?: boolean
  tracked_changes_count: number
  request?: {
    id: number
    layer_id: string
    article_id: string
  }
  iteration: number
  created_at: string
  dismissed_reason: InformalFeedbackReviewDismissReason | null
}

export interface SubmittedInformalFeedbackReview extends Omit<InformalFeedbackReview, 'score' | 'submitted_at'> {
  score: InformalFeedbackReviewScore
  submitted_at: string
}

export interface InformalFeedbackRequestCounts {
  tracked_changes_count: number
  todos_count: number
  comments_count: number
}

export interface CountGroups {
  users: { [userId: number]: InformalFeedbackRequestCounts }
}

export interface Counts extends InformalFeedbackRequestCounts {
  groups: CountGroups
}

export interface CollabItemsCounts {
  todos: number
  comments: number
}

export interface IfrUpdateMessage {
  request_id: number
}

export enum ReviewType {
  Review = 'Review',
  Approval = 'Approval',
  PrivateReview = 'PrivateReview',
  PrivateApproval = 'PrivateApproval',
  ReadReceipt = 'ReadReceipt',
  Suggestion = 'Suggestion',
}

export interface InformalFeedbackRequest {
  id: number
  section_uuid: string | null
  closed_on: string | null
  created_at: string
  company_key: string
  state: InformalFeedbackRequestState
  ends_on: string
  article: InformalFeedbackRequestArticle
  design: InformalFeedbackRequestDesign
  almanac_file: InformalFeedbackRequestAlmanacFile
  requester: InformalFeedbackRequestUserCard
  reviews: InformalFeedbackReview[]
  acknowledged_last_transition: boolean
  key: string
  reviewers_comments_count: number
  reviewers_todos_count: number
  new_reviewers_comments_count: number
  new_reviewers_todos_count: number
  title: string
  type: ReviewType
  description: string | null
  html_description: string | null
  user_completed?: boolean
  cleanup_dismissed?: boolean
  layer: LayerSummary
  __type: 'InformalFeedbackRequest'

  // todo:
  // layer_id is no longer present in the serializer, but this type is being used
  // for inbox threads, which rely on the layer_id being present. We should
  // remove this and fix the type defs for the inbox threads.
  layer_id?: string
  counts: Counts
  collab_items_counts?: CollabItemsCounts
  reviewer_ids: number[]
  last_viewed: { [userId: number]: string }
  viewers: InformalFeedbackRequestViewer[]
  automated: boolean
  suggestion: boolean
}

export interface InformalFeedbackReviewReviewer {
  id: number
  required: boolean
}

export interface InformalFeedbackReviewEmailReviewer {
  email: string
  required: boolean
}

export interface InformalFeedbackReviewChannelReviewer {
  id: number
  required: boolean
}

export interface CreateInformalFeedbackReviewsInput {
  addMembers: boolean
  subject: string
  message: string
  htmlMessage?: string
  reviewers: InformalFeedbackReviewReviewer[]
  emailReviewers: InformalFeedbackReviewEmailReviewer[]
  channelReviewers: InformalFeedbackReviewChannelReviewer[]
  permission: 'edit' | 'comment' | 'read'
}

export interface CreateInformalFeedbackViewerInput {
  addMembers: boolean
  subject: string
  message: string
  viewers: number[]
  emailViewers: string[]
  channelViewers: number[]
}

export interface UpdateInformalFeedbackRequestInput {
  endsOn?: string
  cleanupDismissed?: boolean
}

export interface ExternalResourceInput {
  title: string
  external_link: string
  external_file_icon: string
  workspace_id: number
}

export interface CreateInformalFeedbackRequestInput extends CreateInformalFeedbackReviewsInput {
  endsOn: string
  addMembers: boolean
  layerId?: string
  viewers?: number[]
  emailViewers?: string[]
  channelViewers?: number[]
  sectionUUID?: string
  reviewTemplateId?: number
}

export enum InformalFeedbackReviewScore {
  NeedsMajorChanges = 10,
  NeedsChanges = 20,
  Approved = 30,
}

export enum InformalFeedbackReviewDismissReason {
  Declined = 'declined',
  RequestedAnotherReview = 'requested_another_review',
  RemovedFromReview = 'removed_from_review',
}

export interface SubmitInformalFeedbackReviewInput {
  message: string
  score: InformalFeedbackReviewScore | null
}

export interface DismissInformalFeedbackReviewInput {
  message?: string
  reason: InformalFeedbackReviewDismissReason
  include_request?: boolean
}

export interface UpdateInformalFeedbackReviewInput {
  required: boolean
}

export interface DecisionPayload {
  message: string
  comments?: number[]
}

export interface Decision {
  message: string
  comments: Comment[]
}

export interface CloseInformalFeedbackRequestInput {
  message: string
  decision?: DecisionPayload
}

export interface QueryOptions extends PaginationParams {
  states?: InformalFeedbackRequestState[]
  automated?: boolean
}

export const informalFeedbackReviewValueMapping: { [key: string]: string } = {
  [InformalFeedbackReviewScore.NeedsMajorChanges]: 'score10',
  [InformalFeedbackReviewScore.NeedsChanges]: 'score20',
  [InformalFeedbackReviewScore.Approved]: 'score30',
  [InformalFeedbackReviewDismissReason.Declined]: InformalFeedbackReviewDismissReason.Declined,
}

export const InformalFeedbackRequestsClient = {
  index: async (articleKey: string, layerId?: string, { items, page, states, automated }: QueryOptions = {}) => {
    let url = `/articles/${articleKey}`
    if (layerId) {
      url = `${url}/layers/${layerId}`
    }
    url = `${url}/informal_feedback_requests`
    return sendToApi<{ informal_feedback_requests: InformalFeedbackRequest[] }>(url, {
      method: 'GET',
      params: {
        items,
        page,
        states: states?.join(','),
        automated,
      },
    })
  },

  show: async (id: number) => {
    return sendToApi<{ informal_feedback_request: InformalFeedbackRequest }>(`/informal_feedback_requests/${id}`, {
      method: 'GET',
    })
  },

  // Allow articleKey to be undefined to indicate that the review's article needs to be created too
  create: async (articleKey: string, input: CreateInformalFeedbackRequestInput) => {
    const {
      subject,
      message,
      htmlMessage,
      endsOn,
      reviewers,
      emailReviewers,
      channelReviewers,
      permission,
      addMembers,
      layerId,
      viewers,
      channelViewers,
      emailViewers,
      sectionUUID,
      reviewTemplateId,
    } = input

    return sendToApi<{ informal_feedback_request: InformalFeedbackRequest }>(
      `/articles/${articleKey}/informal_feedback_requests`,
      {
        method: 'POST',
        body: {
          subject,
          message,
          html_message: htmlMessage,
          ends_on: endsOn,
          reviewers,
          email_reviewers: emailReviewers,
          channel_reviewers: channelReviewers,
          viewers,
          email_viewers: emailViewers,
          channel_viewers: channelViewers,
          permission,
          add_members: addMembers,
          layer_id: layerId,
          section_uuid: sectionUUID,
          review_template_id: reviewTemplateId,
        },
      },
    )
  },

  createForAsset: async (
    workspaceId: number,
    asset: ExternalResourceInput,
    input: CreateInformalFeedbackRequestInput,
  ) => {
    const {
      subject,
      message,
      endsOn,
      reviewers,
      emailReviewers,
      channelReviewers,
      permission,
      addMembers,
      layerId,
      viewers,
      channelViewers,
      emailViewers,
      sectionUUID,
      reviewTemplateId,
    } = input

    return sendToApi<{ informal_feedback_request: InformalFeedbackRequest }>(
      `/w/${workspaceId}/informal_feedback_requests`,
      {
        method: 'POST',
        body: {
          subject,
          message,
          ends_on: endsOn,
          reviewers,
          email_reviewers: emailReviewers,
          channel_reviewers: channelReviewers,
          viewers,
          email_viewers: emailViewers,
          channel_viewers: channelViewers,
          permission,
          add_members: addMembers,
          layer_id: layerId,
          asset,
          section_uuid: sectionUUID,
          review_template_id: reviewTemplateId,
        },
      },
    )
  },

  update: async (id: number, input: UpdateInformalFeedbackRequestInput) => {
    const { endsOn, cleanupDismissed } = input

    return sendToApi<{ informal_feedback_request: InformalFeedbackRequest }>(`/informal_feedback_requests/${id}`, {
      method: 'PUT',
      body: { ends_on: endsOn, cleanup_dismissed: cleanupDismissed },
    })
  },

  close: async (id: number, input: CloseInformalFeedbackRequestInput) => {
    const { message, decision } = input
    return sendToApi<{ informal_feedback_request: InformalFeedbackRequest }>(
      `/informal_feedback_requests/${id}/close`,
      {
        method: 'POST',
        body: { message, decision },
      },
    )
  },

  submitReview: async (id: number, input: SubmitInformalFeedbackReviewInput) => {
    const { message, score } = input

    return sendToApi<{ informal_feedback_review: InformalFeedbackReview }>(`/informal_feedback_reviews/${id}`, {
      method: 'PUT',
      body: { message, score },
    })
  },

  updateReview: async (id: number, input: UpdateInformalFeedbackReviewInput) => {
    const { required } = input

    return sendToApi<{ informal_feedback_review: InformalFeedbackReview }>(`/informal_feedback_reviews/${id}`, {
      method: 'PATCH',
      body: { required },
    })
  },

  dismissReview: async (id: number, input: DismissInformalFeedbackReviewInput) => {
    return sendToApi<{ informal_feedback_review: InformalFeedbackReview }>(`/informal_feedback_reviews/${id}`, {
      method: 'DELETE',
      body: input,
    })
  },

  sendReviewReminder: async (id: number) => {
    return sendToApi(`/informal_feedback_reviews/${id}/reminders`, {
      method: 'POST',
    })
  },

  sendReviewReminders: async (ids: number[]) => {
    return sendToApi(`/informal_feedback_review_reminders/batch_create`, {
      method: 'POST',
      body: { ids },
    })
  },

  createReviews: async (id: number, input: CreateInformalFeedbackReviewsInput) => {
    const { subject, message, reviewers, emailReviewers, channelReviewers, permission, addMembers } = input

    return sendToApi<{ informal_feedback_request: InformalFeedbackRequest }>(
      `/informal_feedback_requests/${id}/reviews`,
      {
        method: 'POST',
        body: {
          subject,
          message,
          reviewers,
          email_reviewers: emailReviewers,
          channel_reviewers: channelReviewers,
          permission,
          add_members: addMembers,
        },
      },
    )
  },

  getTimelineEvents: async (id: number, timestamp?: string) => {
    return sendToApi<{ timeline: Timeline }>(`/informal_feedback_requests/${id}/timeline`, {
      params: { timestamp },
    })
  },

  createViewers: async (id: number, input: CreateInformalFeedbackViewerInput) => {
    const { subject, message, viewers, emailViewers, channelViewers, addMembers } = input

    return sendToApi<{ informal_feedback_request: InformalFeedbackRequest }>(
      `/informal_feedback_requests/${id}/viewers`,
      {
        method: 'POST',
        body: {
          subject,
          message,
          viewers,
          email_viewers: emailViewers,
          channel_viewers: channelViewers,
          add_members: addMembers,
        },
      },
    )
  },

  removeViewer: async (id: number) => {
    return sendToApi(`/informal_feedback_viewers/${id}`, { method: 'DELETE' })
  },
}
